import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import gameService from '../../services/gameService';
import Modal from '../../components/Modal';
import MasterProviderImportPreview from '../../components/MasterProviderImportPreview';
import Pagination from '../../components/Pagination';
import SortableHeader from '../../components/SortableHeader';
import { SearchIcon, EditIcon, TrashIcon, CopyIcon, EyeIcon, AlertIcon, CheckCircleIcon, UploadIcon } from '../../components/Icons';
import SearchableSelect from '../../components/SearchableSelect';
import { normalizeCredentialFields } from '../../utils/credentialFields';

const EMPTY_FILTERS = { shortcode: '', code: '', category: '', source: '', type: '', currency: '', country: '' };

const FilterSearch = ({ name, label, value, onChange }) => (
  <div className="search-container" style={{ flex: '1 1 220px', maxWidth: 'none' }}>
    <div className="search-icon-wrapper"><SearchIcon /></div>
    <input type="text" name={name} aria-label={`Filter by ${label}`} className="search-input" placeholder={`Search ${label}...`} value={value} onChange={onChange} />
  </div>
);

const FilterSelect = ({ label, name, value, onChange, options, placeholder }) => (
  <div style={{ flex: '1 1 140px', minWidth: 0 }}>
    <select
      name={name}
      value={value}
      onChange={onChange}
      aria-label={`Filter by ${label.toLowerCase()}`}
      style={{
        width: '100%',
        padding: '0.45rem 0.65rem',
        border: '1px solid var(--border-light)',
        borderRadius: 'var(--radius-md)',
        backgroundColor: 'var(--bg-card)',
        color: 'var(--text-primary)',
        fontSize: '0.8125rem'
      }}
    >
      <option value="">{placeholder || `All ${label}`}</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const formatUtcOffsetTimezone = (offset) => {
  const numericOffset = Number(offset);
  if (!Number.isFinite(numericOffset)) return '';

  const absoluteOffset = Math.abs(numericOffset);
  const offsetLabel = Number.isInteger(absoluteOffset)
    ? String(absoluteOffset)
    : String(absoluteOffset).replace(/0+$/, '').replace(/\.$/, '');

  return `UTC ${numericOffset >= 0 ? '+' : '-'}${offsetLabel}`;
};

const EXTRA_TIMEZONE_OFFSETS = [-9.5, -3.5, 3.5, 4.5, 5.5, 5.75, 6.5, 8.75, 9.5, 10.5, 12.75, 13.75];
const TIMEZONE_OPTIONS = [
  ...new Set([
    ...Array.from({ length: 27 }, (_, index) => index - 12),
    ...EXTRA_TIMEZONE_OFFSETS
  ])
].sort((a, b) => a - b).map(formatUtcOffsetTimezone);

const COUNTRY_TIMEZONE_DEFAULTS = {
  AU: formatUtcOffsetTimezone(10),
  BD: formatUtcOffsetTimezone(6),
  CN: formatUtcOffsetTimezone(8),
  EU: formatUtcOffsetTimezone(1),
  HK: formatUtcOffsetTimezone(8),
  ID: formatUtcOffsetTimezone(7),
  IN: formatUtcOffsetTimezone(5.5),
  JP: formatUtcOffsetTimezone(9),
  KH: formatUtcOffsetTimezone(7),
  KR: formatUtcOffsetTimezone(9),
  LA: formatUtcOffsetTimezone(7),
  MM: formatUtcOffsetTimezone(6.5),
  MO: formatUtcOffsetTimezone(8),
  MY: formatUtcOffsetTimezone(8),
  NZ: formatUtcOffsetTimezone(12),
  PH: formatUtcOffsetTimezone(8),
  PK: formatUtcOffsetTimezone(5),
  SG: formatUtcOffsetTimezone(8),
  TH: formatUtcOffsetTimezone(7),
  TW: formatUtcOffsetTimezone(8),
  US: formatUtcOffsetTimezone(-5),
  VN: formatUtcOffsetTimezone(7)
};

const normalizeTimezoneOption = (value = '') => {
  const normalizedValue = String(value || '').trim().toUpperCase();
  if (!normalizedValue) return '';
  if (normalizedValue === 'UTC') return formatUtcOffsetTimezone(0);

  const match = normalizedValue.match(/^UTC\s*([+-])\s*(\d{1,2})(?:\.(\d{1,2})|:([0-5]\d))?$/);
  if (!match) return '';

  const hour = parseInt(match[2], 10);
  const decimalOffset = match[3] ? Number(`0.${match[3]}`) : 0;
  const minuteOffset = match[4] ? parseInt(match[4], 10) / 60 : 0;
  const absoluteOffset = hour + decimalOffset + minuteOffset;
  const offset = Number(`${match[1]}${absoluteOffset}`);
  return offset >= -12 && offset <= 14 ? formatUtcOffsetTimezone(offset) : '';
};

const TimezoneSelect = ({ label, name, value, onChange, disabled }) => (
  <div>
    <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600 }}>{label}</label>
    <select
      name={name}
      className="input"
      value={value}
      onChange={onChange}
      disabled={disabled}
    >
      <option value="">Select Timezone</option>
      {TIMEZONE_OPTIONS.map((timezone) => (
        <option key={timezone} value={timezone}>{timezone}</option>
      ))}
    </select>
  </div>
);

const tableHeaderStyle = {
  padding: '1rem',
  textAlign: 'left',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  whiteSpace: 'nowrap'
};

const formatFileSize = (size = 0) => {
  if (!size) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB'];
  let value = size;
  let index = 0;

  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }

  return `${value >= 10 || index === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[index]}`;
};

const getDocumentKey = (doc) => doc?.object_key || doc?.file_path || doc?.file_name || '';

const normalizeDocuments = (documents = []) => (
  Array.isArray(documents)
    ? documents.map((doc) => ({
        ...doc,
        label: doc?.label || ''
      }))
    : []
);

const getSetupStatus = (provider) => {
  const missing = [];
  if (normalizeCredentialFields(provider.bo_credential_keys).length === 0) missing.push('BO');
  if (normalizeCredentialFields(provider.api_credential_keys).length === 0) missing.push('API');
  if (!Array.isArray(provider.callback_urls) || provider.callback_urls.length === 0) missing.push('Callback');
  return missing;
};

const REPORTING_DEFAULTS = {
  reporting_game_provider_timezone: '',
  reporting_bo_timezone: '',
  reporting_provider_bo_link: '',
  reporting_company_win_indicator: 'positive',
  reporting_player_win_indicator: 'negative',
  reporting_remarks: ''
};

const normalizeWinIndicator = (value) => (
  ['positive', 'negative'].includes(value) ? value : ''
);

const getCountryCode = (country) => String(country?.code || '').trim().toUpperCase();

const getCountryTimezoneDefault = (country) => (
  normalizeTimezoneOption(country?.timezone || country?.time_zone || COUNTRY_TIMEZONE_DEFAULTS[getCountryCode(country)])
);

const getCountryById = (countries, countryId) => (
  countries.find((country) => country._id === countryId)
);

const getReportingWinIndicator = (provider, side) => (
  provider?.[`reporting_${side}_win_indicator`] || provider?.[`reporting_${side}_winlose_indicator`]
);

const applyReportingDefaults = (fields = {}, country) => {
  const defaultTimezone = getCountryTimezoneDefault(country);

  return {
    ...fields,
    reporting_game_provider_timezone: normalizeTimezoneOption(fields.reporting_game_provider_timezone) || defaultTimezone || '',
    reporting_bo_timezone: normalizeTimezoneOption(fields.reporting_bo_timezone) || defaultTimezone || '',
    reporting_company_win_indicator: normalizeWinIndicator(fields.reporting_company_win_indicator) || 'positive',
    reporting_player_win_indicator: normalizeWinIndicator(fields.reporting_player_win_indicator) || 'negative'
  };
};

const normalizeReportingFields = (provider = {}, country = provider.country) => applyReportingDefaults({
  reporting_game_provider_timezone: provider.reporting_game_provider_timezone,
  reporting_bo_timezone: provider.reporting_bo_timezone,
  reporting_provider_bo_link: String(provider.reporting_provider_bo_link || '').trim(),
  reporting_company_win_indicator: getReportingWinIndicator(provider, 'company'),
  reporting_player_win_indicator: getReportingWinIndicator(provider, 'player'),
  reporting_remarks: String(provider.reporting_remarks || '').trim()
}, country);

const getCountryReportingDefaultPatch = (currentData = {}, countries = [], countryId = '') => {
  const previousTimezoneDefault = getCountryTimezoneDefault(getCountryById(countries, currentData.country));
  const nextTimezoneDefault = getCountryTimezoneDefault(getCountryById(countries, countryId));
  const shouldUseNextTimezoneDefault = (field) => {
    const currentTimezone = normalizeTimezoneOption(currentData[field]);
    return !currentTimezone || (!!previousTimezoneDefault && currentTimezone === previousTimezoneDefault);
  };

  return {
    reporting_game_provider_timezone: shouldUseNextTimezoneDefault('reporting_game_provider_timezone')
      ? nextTimezoneDefault
      : currentData.reporting_game_provider_timezone,
    reporting_bo_timezone: shouldUseNextTimezoneDefault('reporting_bo_timezone')
      ? nextTimezoneDefault
      : currentData.reporting_bo_timezone,
    reporting_company_win_indicator: normalizeWinIndicator(currentData.reporting_company_win_indicator) || 'positive',
    reporting_player_win_indicator: normalizeWinIndicator(currentData.reporting_player_win_indicator) || 'negative'
  };
};

const SetupIndicator = ({ provider }) => {
  const missing = getSetupStatus(provider);

  if (missing.length === 0) {
    return (
      <span title="BO credential keys, API credential keys, and callback URLs are configured" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.25rem 0.55rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700, backgroundColor: 'rgba(16, 185, 129, 0.1)', color: 'var(--success)', whiteSpace: 'nowrap' }}>
        <CheckCircleIcon size={14} />
        Ready
      </span>
    );
  }

  return (
    <div title={`Missing: ${missing.join(', ')}`} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap', maxWidth: '220px' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700, backgroundColor: 'rgba(245, 158, 11, 0.12)', color: '#B45309', whiteSpace: 'nowrap' }}>
        <AlertIcon size={14} />
        {missing.length} missing
      </span>
      {missing.map((label) => (
        <span key={label} style={{ padding: '0.2rem 0.45rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 700, backgroundColor: 'rgba(var(--primary-rgb), 0.08)', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
          {label}
        </span>
      ))}
    </div>
  );
};


const MasterProviders = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [masterProviders, setMasterProviders] = useState([]);
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sources, setSources] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState({ sortBy: '', sortOrder: 'asc' });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState('');
  const [importError, setImportError] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [skipImportIssues, setSkipImportIssues] = useState(false);
  const [importResolutions, setImportResolutions] = useState({});
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    provider_id: '', source: '',
    type: 'Seamless', currency: '', country: '',
    shortcode: '',
    backend_code: '',
    costing_rates: 0, settlement_period: 'Monthly', is_carry_forward: false, is_active: true,
    topup_options: false,
    ...REPORTING_DEFAULTS,
    group_links: [{ name: '', url: '' }],
    api_credential_keys: [],
    bo_credential_keys: [],
    callback_urls: [],
    documents: []
  });
  const [credentialKeyInput, setCredentialKeyInput] = useState('');
  const [credentialDefaultInput, setCredentialDefaultInput] = useState('');
  const [boCredentialKeyInput, setBoCredentialKeyInput] = useState('');
  const [boCredentialDefaultInput, setBoCredentialDefaultInput] = useState('');
  const [callbackNameInput, setCallbackNameInput] = useState('');
  const [callbackPathInput, setCallbackPathInput] = useState('');

  const fetchDropdowns = useCallback(async () => {
    const [providersRes, sourcesRes, currenciesRes, countriesRes, categoriesRes] = await Promise.allSettled([
      gameService.getProviders({ limit: 1000, is_active: true }),
      gameService.getSources({ limit: 1000 }),
      gameService.getCurrencies({ limit: 1000 }),
      gameService.getCountries({ limit: 1000 }),
      gameService.getCategories({ limit: 1000 })
    ]);
    [
      [providersRes, setProviders],
      [sourcesRes, setSources],
      [currenciesRes, setCurrencies],
      [countriesRes, setCountries],
      [categoriesRes, setCategories]
    ].forEach(([result, setOptions]) => {
      if (result.status === 'fulfilled' && result.value.success) setOptions(result.value.data);
    });
  }, []);

  useEffect(() => { fetchDropdowns(); }, [fetchDropdowns]);

  const fetchMasterProviders = useCallback(async (page = pagination.page, search = searchTerm, activeFilters = filters, limit = pagination.limit, activeSort = sort, clearList = false) => {
    try {
      setLoading(true);
      setError(null);
      if (clearList) setMasterProviders([]);
      const params = { page, limit, search, sortBy: activeSort.sortBy, sortOrder: activeSort.sortOrder, ...activeFilters };
      Object.keys(params).forEach(k => { if (params[k] === '') delete params[k]; });
      const res = await gameService.getMasterProviders(params);
      if (res.success) {
        setMasterProviders(res.data);
        setPagination(prev => ({ ...prev, page: res.pagination.page, total: res.pagination.total, pages: res.pagination.pages }));
      }
    } catch (err) {
      setError('An error occurred while fetching master providers');
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.page, searchTerm, filters, sort]);

  useEffect(() => {
    const t = setTimeout(() => fetchMasterProviders(1, searchTerm, filters, pagination.limit, sort), 400);
    return () => clearTimeout(t);
  }, [searchTerm, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const canView = hasPermission('master_providers', 'view');
  const canCreate = hasPermission('master_providers', 'create');
  const canEdit = hasPermission('master_providers', 'edit');
  const canDelete = hasPermission('master_providers', 'delete');
  const canViewFinancial = hasPermission('master_providers', 'view_financial');
  const canEditFinancial = hasPermission('master_providers', 'edit_financial');
  const canViewReporting = hasPermission('master_providers', 'view_reporting');
  const canEditReporting = hasPermission('master_providers', 'edit_reporting');
  const canImport = hasPermission('master_providers', 'import') && canViewFinancial;
  const canExport = canView && canViewFinancial && hasPermission('master_providers', 'export');
  const showActions = canView || canCreate || canEdit || canDelete;

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters(EMPTY_FILTERS);
  };

  const handleSortChange = (sortBy, sortOrder) => {
    const nextSort = { sortBy, sortOrder };
    if (sort.sortBy === nextSort.sortBy && sort.sortOrder === nextSort.sortOrder) return;

    setSort(nextSort);
    fetchMasterProviders(1, searchTerm, filters, pagination.limit, nextSort, true);
  };

  const hasActiveFilters = searchTerm || Object.values(filters).some(v => v);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'source') {
      const source = sources.find(s => s._id === value);
      setFormData(prev => ({
        ...prev,
        source: value,
        group_links: source?.group_links?.length > 0 ? normalizeGroupLinks(source.group_links) : [{ name: '', url: '' }]
      }));
      return;
    }
    if (name === 'country') {
      setFormData(prev => ({
        ...prev,
        country: value,
        ...getCountryReportingDefaultPatch(prev, countries, value)
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? (value === '' ? '' : parseFloat(value)) : value)
    }));
  };

  const normalizeGroupLinks = (links = []) => (
    Array.isArray(links)
      ? links.map((link, index) => (
          typeof link === 'string'
            ? { name: `Link ${index + 1}`, url: link }
            : { name: link?.name || `Link ${index + 1}`, url: link?.url || link?.link || '' }
        ))
      : []
  );

  const handleGroupLinkChange = (field, value, index) => {
    setFormData(prev => {
      const groupLinks = [...(prev.group_links || [{ name: '', url: '' }])];
      groupLinks[index] = { ...(groupLinks[index] || {}), [field]: value };
      return { ...prev, group_links: groupLinks };
    });
  };

  const addGroupLink = () => {
    setFormData(prev => ({
      ...prev,
      group_links: [...(prev.group_links || []), { name: '', url: '' }]
    }));
  };

  const removeGroupLink = (index) => {
    setFormData(prev => {
      const groupLinks = (prev.group_links || []).filter((_, i) => i !== index);
      return { ...prev, group_links: groupLinks.length ? groupLinks : [{ name: '', url: '' }] };
    });
  };

  const resetGroupLinksToSource = () => {
    const source = sources.find(s => s._id === formData.source);
    setFormData(prev => ({
      ...prev,
      group_links: source?.group_links?.length > 0 ? normalizeGroupLinks(source.group_links) : [{ name: '', url: '' }]
    }));
  };

  const addCredentialKey = () => {
    const key = credentialKeyInput.trim();
    if (!key) return;
    setFormData(prev => {
      const fields = normalizeCredentialFields(prev.api_credential_keys);
      const withoutCurrent = fields.filter(field => field.key !== key);
      return {
        ...prev,
        api_credential_keys: [...withoutCurrent, { key, defaultValue: credentialDefaultInput }]
      };
    });
    setCredentialKeyInput('');
    setCredentialDefaultInput('');
  };

  const removeCredentialKey = (key) => {
    setFormData(prev => ({
      ...prev,
      api_credential_keys: normalizeCredentialFields(prev.api_credential_keys).filter(field => field.key !== key)
    }));
  };

  const updateCredentialDefault = (key, defaultValue) => {
    setFormData(prev => ({
      ...prev,
      api_credential_keys: normalizeCredentialFields(prev.api_credential_keys).map(field => (
        field.key === key ? { ...field, defaultValue } : field
      ))
    }));
  };

  const addBoCredentialKey = () => {
    const key = boCredentialKeyInput.trim();
    if (!key) return;
    setFormData(prev => {
      const fields = normalizeCredentialFields(prev.bo_credential_keys);
      const withoutCurrent = fields.filter(field => field.key !== key);
      return {
        ...prev,
        bo_credential_keys: [...withoutCurrent, { key, defaultValue: boCredentialDefaultInput }]
      };
    });
    setBoCredentialKeyInput('');
    setBoCredentialDefaultInput('');
  };

  const removeBoCredentialKey = (key) => {
    setFormData(prev => ({
      ...prev,
      bo_credential_keys: normalizeCredentialFields(prev.bo_credential_keys).filter(field => field.key !== key)
    }));
  };

  const updateBoCredentialDefault = (key, defaultValue) => {
    setFormData(prev => ({
      ...prev,
      bo_credential_keys: normalizeCredentialFields(prev.bo_credential_keys).map(field => (
        field.key === key ? { ...field, defaultValue } : field
      ))
    }));
  };

  const addCallbackUrl = () => {
    const name = callbackNameInput.trim();
    const path = callbackPathInput.trim();
    if (!name || !path) return;
    setFormData(prev => {
      const existing = (prev.callback_urls || []).filter(cb => cb.name !== name);
      return { ...prev, callback_urls: [...existing, { name, path }] };
    });
    setCallbackNameInput('');
    setCallbackPathInput('');
  };

  const removeCallbackUrl = (name) => {
    setFormData(prev => ({
      ...prev,
      callback_urls: (prev.callback_urls || []).filter(cb => cb.name !== name)
    }));
  };

  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const queuedDocs = files.map((file, index) => ({
      temp_key: `${Date.now()}-${index}-${file.name}`,
      label: '',
      original_name: file.name,
      file_name: file.name,
      file_path: '',
      object_key: '',
      mime_type: file.type,
      size: file.size,
      uploaded_at: null,
      file
    }));

    setFormData(prev => ({
      ...prev,
      documents: [...(prev.documents || []), ...queuedDocs]
    }));

    e.target.value = '';
  };

  const handleDocumentLabelChange = (documentKey, label) => {
    setFormData(prev => ({
      ...prev,
      documents: (prev.documents || []).map(doc => (
        getDocumentKey(doc) === documentKey ? { ...doc, label } : doc
      ))
    }));
  };

  const resetForm = () => ({
    provider_id: '',
    source: '',
    type: 'Seamless',
    currency: '',
    country: '',
    shortcode: '',
    backend_code: '',
    costing_rates: 0,
    settlement_period: 'Monthly',
    is_carry_forward: false,
    is_active: true,
    topup_options: false,
    ...REPORTING_DEFAULTS,
    group_links: [{ name: '', url: '' }],
    api_credential_keys: [],
    bo_credential_keys: [],
    callback_urls: [],
    documents: []
  });
  const closeModal = useCallback(() => {
    if (submitting) return;
    setIsModalOpen(false);
    setEditId(null);
    setFormData(resetForm());
    setCredentialKeyInput('');
    setCredentialDefaultInput('');
    setBoCredentialKeyInput('');
    setBoCredentialDefaultInput('');
    setCallbackNameInput('');
    setCallbackPathInput('');
  }, [submitting]);

  const resetImportState = () => {
    setImportFile(null);
    setImportPreview(null);
    setSkipImportIssues(false);
    setImportResolutions({});
    setImportResult('');
    setImportError('');
  };

  const closeImportModal = useCallback(() => {
    if (importing) return;
    setIsImportModalOpen(false);
    resetImportState();
  }, [importing]);

  const removeDocument = (doc) => {
    const documentKey = getDocumentKey(doc);
    if (!documentKey) return;

    setFormData(prev => ({
      ...prev,
      documents: (prev.documents || []).filter(currentDoc => getDocumentKey(currentDoc) !== documentKey)
    }));
  };

  const openAddModal = () => {
    setEditId(null);
    setFormData(resetForm());
    setCredentialKeyInput('');
    setCredentialDefaultInput('');
    setBoCredentialKeyInput('');
    setBoCredentialDefaultInput('');
    setCallbackNameInput('');
    setCallbackPathInput('');
    setIsModalOpen(true);
  };
  const openCloneModal = (p) => {
    setEditId(null);
    setFormData({
      provider_id: p.provider_id?._id || '',
      source: p.source?._id || '',
      type: p.type || 'Seamless',
      currency: p.currency?._id || '',
      country: p.country?._id || '',
      shortcode: '',
      backend_code: p.backend_code || '',
      costing_rates: p.costing_rates ?? 0,
      settlement_period: p.settlement_period || 'Monthly',
      is_carry_forward: !!p.is_carry_forward,
      is_active: p.is_active,
      topup_options: !!p.topup_options,
      ...normalizeReportingFields(p),
      group_links: Array.isArray(p.group_links) ? (p.group_links.length > 0 ? normalizeGroupLinks(p.group_links) : [{ name: '', url: '' }]) : (p.source?.group_links?.length > 0 ? normalizeGroupLinks(p.source.group_links) : [{ name: '', url: '' }]),
      api_credential_keys: normalizeCredentialFields(p.api_credential_keys),
      bo_credential_keys: normalizeCredentialFields(p.bo_credential_keys),
      callback_urls: Array.isArray(p.callback_urls) ? p.callback_urls.map(cb => ({ name: cb.name, path: cb.path })) : [],
      documents: []
    });
    setCredentialKeyInput('');
    setCredentialDefaultInput('');
    setBoCredentialKeyInput('');
    setBoCredentialDefaultInput('');
    setCallbackNameInput('');
    setCallbackPathInput('');
    setIsModalOpen(true);
  };
  const openEditModal = (p) => {
    const documents = normalizeDocuments(p.documents);
    setEditId(p._id);
    setFormData({ 
      provider_id: p.provider_id?._id || '', 
      source: p.source?._id || '', 
      type: p.type || 'Seamless', 
      currency: p.currency?._id || '', 
      country: p.country?._id || '', 
      shortcode: p.shortcode || '',
      backend_code: p.backend_code || '',
      costing_rates: p.costing_rates ?? 0,
      settlement_period: p.settlement_period || 'Monthly', 
      is_carry_forward: !!p.is_carry_forward,
      is_active: p.is_active,
      code: p.code || '',
      topup_options: !!p.topup_options,
      ...normalizeReportingFields(p),
      group_links: Array.isArray(p.group_links) ? (p.group_links.length > 0 ? normalizeGroupLinks(p.group_links) : [{ name: '', url: '' }]) : (p.source?.group_links?.length > 0 ? normalizeGroupLinks(p.source.group_links) : [{ name: '', url: '' }]),
      api_credential_keys: normalizeCredentialFields(p.api_credential_keys),
      bo_credential_keys: normalizeCredentialFields(p.bo_credential_keys),
      callback_urls: Array.isArray(p.callback_urls) ? p.callback_urls : [],
      documents
    });
    setCredentialKeyInput('');
    setCredentialDefaultInput('');
    setBoCredentialKeyInput('');
    setBoCredentialDefaultInput('');
    setCallbackNameInput('');
    setCallbackPathInput('');
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const payload = {
        ...formData,
        provider_id: formData.provider_id,
        source: formData.source,
        currency: formData.currency,
        country: formData.country,
        topup_options: !!formData.topup_options,
        ...normalizeReportingFields(formData, getCountryById(countries, formData.country)),
        group_links: Array.isArray(formData.group_links)
          ? formData.group_links
              .map((link, index) => {
                if (typeof link === 'string') return { name: `Link ${index + 1}`, url: link.trim() };
                return { name: String(link?.name || '').trim(), url: String(link?.url || '').trim() };
              })
              .filter(link => link.url)
          : [],
        api_credential_keys: normalizeCredentialFields(formData.api_credential_keys),
        bo_credential_keys: normalizeCredentialFields(formData.bo_credential_keys),
        callback_urls: Array.isArray(formData.callback_urls) ? formData.callback_urls : [],
        documents: normalizeDocuments(formData.documents)
      };
      delete payload.shortcode;
      const response = editId ? await gameService.updateMasterProvider(editId, payload) : await gameService.createMasterProvider(payload);
      if (response.success) {
        closeModal();
        fetchMasterProviders();
      }
      else alert(response.message || 'Failed to save master provider');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'An error occurred while saving master provider';
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this master provider configuration?')) return;
    try {
      setLoading(true);
      const res = await gameService.deleteMasterProvider(id);
      if (res.success) fetchMasterProviders();
      else alert(res.message || 'Failed to delete master provider');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'An error occurred while deleting master provider';
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await gameService.exportMasterProviders();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      try {
        link.href = url;
        link.download = `L1-Game-Masterlist-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
      } finally {
        link.remove();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      const data = err.response?.data;
      const details = data instanceof Blob
        ? await data.text().then(JSON.parse).catch(() => null)
        : data;
      alert(details?.message || err.message || 'Failed to export master providers.');
    } finally {
      setExporting(false);
    }
  };

  const handleImportFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setImportFile(file);
    setImportPreview(null);
    setSkipImportIssues(false);
    setImportResolutions({});
    setImportResult('');
    setImportError('');
    if (file) runImport('dry-run', file, false, {});
  };

  const runImport = async (mode, file = importFile, skipIssues = skipImportIssues, resolutions = importResolutions) => {
    if (!file) return;
    if (mode === 'apply' && !importPreview?.confirmationToken) return;
    try {
      setImporting(true);
      setImportError('');
      setImportResult('');
      const confirmationToken = importPreview?.confirmationToken;
      if (mode === 'dry-run') setImportPreview(previous => previous ? { ...previous, confirmationToken: null } : null);
      const res = await gameService.importMasterProviders({ file, mode, confirmationToken, skipIssues, resolutions });
      setImportPreview({ ...res.data.preview, confirmationToken: res.data.confirmation_token });
      if (mode === 'apply') {
        setImportResult(`Import completed. The reviewed changes have been saved.${res.data.summary.skipped ? ` ${res.data.summary.skipped} records were skipped; see Issues for details.` : ''}`);
        fetchMasterProviders(1, searchTerm, filters, pagination.limit, sort, true);
        fetchDropdowns();
      }
    } catch (err) {
      setImportPreview(null);
      setImportError(err.response?.status === 409
        ? 'The database or preview has changed. Compare again before confirming.'
        : err.response?.data?.message || err.message || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  const resolveImportIssues = (resolutions) => {
    setImportResolutions(resolutions);
    runImport('dry-run', importFile, skipImportIssues, resolutions);
  };

  return (
    <div className="game-providers-page">
      {(canCreate || canImport || canExport) && (
        <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
          {canExport && (
            <button className="btn btn-secondary" onClick={handleExport} disabled={exporting} aria-busy={exporting} title="Export all L1 master providers">
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
          )}
          {canImport && (
            <button className="btn btn-secondary" onClick={() => setIsImportModalOpen(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
              <UploadIcon size={16} />
              Import
            </button>
          )}
          {canCreate && (
            <button className="btn btn-primary" onClick={openAddModal}>Add Master Provider</button>
          )}
        </div>
      )}

      <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <FilterSearch name="shortcode" label="shortcode" value={filters.shortcode} onChange={handleFilterChange} />
          <FilterSearch name="code" label="unique ID" value={filters.code} onChange={handleFilterChange} />
          <FilterSearch name="search" label="provider name" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
          <FilterSelect label="Category" name="category" value={filters.category} onChange={handleFilterChange}
            options={categories.map(c => ({ value: c._id, label: c.name }))} placeholder="All Categories" />
          <FilterSelect label="Upline" name="source" value={filters.source} onChange={handleFilterChange}
            options={sources.map(s => ({ value: s._id, label: s.name }))} placeholder="All Uplines" />
          <FilterSelect label="Type" name="type" value={filters.type} onChange={handleFilterChange}
            options={[{ value: 'Seamless', label: 'Seamless' }, { value: 'Transfer', label: 'Transfer' }]} placeholder="All Types" />
          <FilterSelect label="Currency" name="currency" value={filters.currency} onChange={handleFilterChange}
            options={currencies.map(c => ({ value: c._id, label: `${c.code} — ${c.name}` }))} placeholder="All Currencies" />
          <FilterSelect label="Country" name="country" value={filters.country} onChange={handleFilterChange}
            options={countries.map(c => ({ value: c._id, label: `${c.code} — ${c.name}` }))} placeholder="All Countries" />
          {hasActiveFilters && (
            <button onClick={clearFilters} style={{ padding: '0.45rem 0.75rem', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.8125rem' }}>
              ✕ Clear
            </button>
          )}
        </div>
      </div>

      {loading && masterProviders.length === 0 ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>Loading master providers...</div>
      ) : error ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--error)' }}>{error}</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="hidden-horizontal-scrollbar" style={{ overflowX: 'auto', width: '100%' }}>
            <table className="table-striped" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1570px' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--table-header-bg)', borderBottom: '1px solid var(--border-light)' }}>
                  <SortableHeader label="Shortcode" sortKey="shortcode" sort={sort} onSortChange={handleSortChange} style={tableHeaderStyle} />
                  <SortableHeader label="Unique ID" sortKey="code" sort={sort} onSortChange={handleSortChange} style={tableHeaderStyle} />
                  <th style={tableHeaderStyle}>Backend Code</th>
                  <SortableHeader label="Provider" sortKey="provider" sort={sort} onSortChange={handleSortChange} style={tableHeaderStyle} />
                  <SortableHeader label="Category" sortKey="category" sort={sort} onSortChange={handleSortChange} style={tableHeaderStyle} />
                  <th style={tableHeaderStyle}>Sub Category</th>
                  <SortableHeader label="Upline" sortKey="source" sort={sort} onSortChange={handleSortChange} style={tableHeaderStyle} />
                  <th style={tableHeaderStyle}>Type</th>
                  <SortableHeader label="Currency" sortKey="currency" sort={sort} onSortChange={handleSortChange} style={tableHeaderStyle} />
                  <th style={tableHeaderStyle}>Country</th>
                  <th style={tableHeaderStyle}>Documents</th>
                  <th style={tableHeaderStyle}>Setup</th>
                  {canViewFinancial && (
                    <>
                      <th style={tableHeaderStyle}>Rates</th>
                      <th style={tableHeaderStyle}>Settlement</th>
                      <th style={tableHeaderStyle}>Carry Forward</th>
                    </>
                  )}
                  <th style={tableHeaderStyle}>Status</th>
                  {showActions && <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Actions</th>}
                </tr>
              </thead>
            <tbody>
                {masterProviders.length === 0 ? (
                  <tr><td colSpan={(showActions ? 14 : 13) + (canViewFinancial ? 3 : 0)} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No master providers found.</td></tr>
                ) : masterProviders.map((p) => (
                  <tr key={p._id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{p.shortcode || '-'}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{p.code}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{p.backend_code || '-'}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', fontWeight: 600 }}>{p.provider_id?.name || 'Unknown'}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{p.provider_id?.category?.name || '-'}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{p.provider_id?.sub_category?.name || '-'}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{p.source?.name || '-'}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      <span style={{ padding: '0.2rem 0.5rem', backgroundColor: 'rgba(var(--primary-rgb), 0.1)', color: p.type === 'Seamless' ? 'var(--accent)' : 'var(--secondary)', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>{p.type}</span>
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{p.currency?.code || '-'}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{p.country?.code || '-'}</td>
                    <td style={{ padding: '1rem', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                      {(p.documents || []).length === 0 ? (
                        <span style={{ color: 'var(--text-muted)' }}>No documents</span>
                      ) : (
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{p.documents.length} file(s)</span>
                      )}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                      <SetupIndicator provider={p} />
                    </td>
                    {canViewFinancial && (
                    <>
                      <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{p.costing_rates}%</td>
                      <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{p.settlement_period}</td>
                      <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      <span style={{ padding: '0.25rem 0.625rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600, backgroundColor: p.is_carry_forward ? 'rgba(16, 185, 129, 0.1)' : 'rgba(var(--primary-rgb), 0.08)', color: p.is_carry_forward ? 'var(--success)' : 'var(--text-muted)' }}>
                        {p.is_carry_forward ? 'Yes' : 'No'}
                      </span>
                      </td>
                    </>
                    )}
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      <span style={{ padding: '0.25rem 0.625rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600, backgroundColor: p.is_active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', color: p.is_active ? 'var(--success)' : 'var(--error)' }}>{p.is_active ? 'Active' : 'Inactive'}</span>
                    </td>
                    {showActions && (
                      <td style={{ padding: '1rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                          {canCreate && (
                            <button
                              className="btn"
                              style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                              onClick={() => openCloneModal(p)}
                            >
                              <CopyIcon size={14} />
                              Clone
                            </button>
                          )}
                          {canView && (
                            <button
                              className="btn"
                              style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                              onClick={() => navigate(`/games/master-providers/${p._id}/view`)}
                            >
                              <EyeIcon size={14} />
                              View
                            </button>
                          )}
                          {canEdit && (
                            <button
                              className="btn"
                              style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                              onClick={() => openEditModal(p)}
                            >
                              <EditIcon size={14} />
                              Edit
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="btn"
                              style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', color: 'var(--error)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                              onClick={() => handleDelete(p._id)}
                            >
                              <TrashIcon size={14} />
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <Pagination pagination={pagination} onPageChange={(pg) => fetchMasterProviders(pg, searchTerm, filters, pagination.limit, sort)} onLimitChange={(l) => {
          setPagination(prev => ({ ...prev, limit: l }));
          fetchMasterProviders(1, searchTerm, filters, l, sort);
        }} loading={loading} itemName="master providers" />
      </div>
      )}

      <Modal isOpen={isImportModalOpen} onClose={closeImportModal} title="Import L1 Master Providers" maxWidth={importPreview ? '1200px' : '760px'}>
        <div style={{ display: 'grid', gap: '1rem' }}>
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            Select a CSV or Excel masterlist to compare it with the database. Review all L1 updates, new L1 records,
            missing L0 records and issues, then confirm to save. Blank values keep existing settings; enter 0 for zero cost.
            Choose CSV or System for each difference; Ignore skips the entire CSV row.
            L1 costing changes also affect merchants using those records. Code is imported as Shortcode; blank keeps the existing number, or assigns a number for a new record. Selling Price is not imported.
          </p>
          <div>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600 }}>CSV or Excel File</label>
            <input
              type="file"
              className="input"
              accept=".csv,.xls,.xlsx"
              onChange={handleImportFileChange}
              disabled={importing}
            />
          </div>

          <label className="l1-import-skip-option">
            <input type="checkbox" checked={skipImportIssues} disabled={importing}
              onChange={event => {
                const skip = event.target.checked;
                setSkipImportIssues(skip);
                setImportPreview(null);
                if (importFile) runImport('dry-run', importFile, skip);
              }} />
            <span><strong>Skip records with issues</strong><small>Compare again automatically and import only valid records. Skipped records remain listed in Issues.</small></span>
          </label>

          {importing && <p role="status">Processing import comparison…</p>}
          {importPreview && <MasterProviderImportPreview key={importFile?.name} preview={importPreview}
            resolutions={importResolutions} onResolve={resolveImportIssues} disabled={importing} />}

          {importError && (
            <div style={{ padding: '0.75rem', borderRadius: 'var(--radius-md)', backgroundColor: 'rgba(239, 68, 68, 0.08)', color: 'var(--error)', fontSize: '0.875rem', fontWeight: 600 }}>
              {importError}
            </div>
          )}

          {importResult && <p role="status" style={{ color: 'var(--success)' }}>{importResult}</p>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', paddingTop: '0.5rem' }}>
            <button type="button" className="btn btn-secondary" onClick={closeImportModal} disabled={importing}>Cancel</button>
            <button type="button" className="btn btn-secondary" onClick={() => runImport('dry-run')} disabled={importing || !importFile}>
              Compare again
            </button>
            <button type="button" className="btn btn-primary" onClick={() => runImport('apply')} disabled={importing || !importPreview?.confirmationToken}>
              {importPreview?.summary.skip_issues && importPreview.summary.skipped ? `Confirm import (skip ${importPreview.summary.skipped})` : 'Confirm import'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isModalOpen} onClose={() => closeModal()} title={editId ? 'Edit Master Provider' : 'Add New Master Provider'}>
        <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div style={{ gridColumn: '1 / span 2', marginBottom: '0.75rem' }}>
            <SearchableSelect 
              label="Provider"
              name="provider_id"
              value={formData.provider_id}
              onChange={handleInputChange}
              required
              placeholder="Select Provider"
              options={providers.map(p => ({
                value: p._id,
                label: `${p.name} (${p.category?.name}${p.sub_category ? ` - ${p.sub_category.name}` : ''})`
              }))}
            />
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <SearchableSelect 
              label="Provider Source"
              name="source"
              value={formData.source}
              onChange={handleInputChange}
              required
              placeholder="Select Source"
              options={sources.map(s => ({ value: s._id, label: `${s.code} — ${s.name}` }))}
            />
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600 }}>Provider Type *</label>
            <select name="type" className="input" value={formData.type} onChange={handleInputChange} required>
              <option value="Seamless">Seamless</option>
              <option value="Transfer">Transfer</option>
            </select>
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600 }}>Currency *</label>
            <select name="currency" className="input" value={formData.currency} onChange={handleInputChange} required>
              <option value="">Select Currency</option>
              {currencies.map(c => <option key={c._id} value={c._id}>{c.currency_code || c.code} — {c.name}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600 }}>Country *</label>
            <select name="country" className="input" value={formData.country} onChange={handleInputChange} required>
              <option value="">Select Country</option>
              {countries.map(c => <option key={c._id} value={c._id}>{c.code} — {c.name}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600 }}>Shortcode</label>
            <input
              type="text"
              name="shortcode"
              className="input"
              value={formData.shortcode}
              readOnly
              placeholder="Assigned automatically when created"
            />
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600 }}>Backend Code</label>
            <input
              type="text"
              name="backend_code"
              className="input"
              value={formData.backend_code}
              onChange={handleInputChange}
              placeholder="e.g. PGSoft"
            />
          </div>
          <div style={{ gridColumn: '1 / span 2', marginBottom: '0.75rem', padding: '1rem', backgroundColor: 'var(--bg-soft)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-primary)' }}>Group Links</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>Defaults from provider source; changes here apply only to this L1 Master Provider.</div>
              </div>
              {formData.source && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}
                  onClick={resetGroupLinksToSource}
                >
                  Reset to Source
                </button>
              )}
            </div>
            {(formData.group_links || [{ name: '', url: '' }]).map((link, index) => (
              <div key={`master-provider-group-link-${index}`} style={{ display: 'grid', gridTemplateColumns: (formData.group_links || []).length > 1 ? 'minmax(140px, 0.35fr) minmax(220px, 1fr) auto' : 'minmax(140px, 0.35fr) minmax(220px, 1fr)', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input
                  type="text"
                  className="input"
                  value={typeof link === 'string' ? `Link ${index + 1}` : (link.name || '')}
                  onChange={(e) => handleGroupLinkChange('name', e.target.value, index)}
                  placeholder="Name"
                />
                <input
                  type="url"
                  className="input"
                  value={typeof link === 'string' ? link : (link.url || '')}
                  onChange={(e) => handleGroupLinkChange('url', e.target.value, index)}
                  placeholder="URL"
                />
                {(formData.group_links || []).length > 1 && (
                  <button type="button" className="btn btn-secondary" style={{ padding: '0 0.75rem' }} onClick={() => removeGroupLink(index)}>×</button>
                )}
              </div>
            ))}
            <button type="button" className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.35rem 0.85rem' }} onClick={addGroupLink}>+ Add Group Link</button>
          </div>
          {canViewFinancial && (
          <div style={{ gridColumn: '1 / span 2', marginBottom: '0.75rem', padding: '1rem', backgroundColor: 'var(--bg-soft)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)' }}>
            <div style={{ fontSize: '0.875rem', fontWeight: 700, marginBottom: '0.85rem', color: 'var(--text-primary)' }}>Financial</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600 }}>Costing Rates (%) *</label>
                <input type="number" name="costing_rates" className="input" value={formData.costing_rates} onChange={handleInputChange} min="0" max="100" step="0.01" required disabled={!canEditFinancial} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600 }}>Settlement Period *</label>
                <select name="settlement_period" className="input" value={formData.settlement_period} onChange={handleInputChange} required disabled={!canEditFinancial}>
                  <option value="Daily">Daily</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Biweekly">Biweekly</option>
                  <option value="Monthly">Monthly</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', backgroundColor: 'var(--bg-card)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)' }}>
              <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Carry Forward</span>
              <label className="switch">
                <input type="checkbox" name="is_carry_forward" checked={!!formData.is_carry_forward} onChange={handleInputChange} disabled={!canEditFinancial} />
                <span className="slider"></span>
              </label>
            </div>
          </div>
          )}
          {canViewReporting && (
          <div style={{ gridColumn: '1 / span 2', marginBottom: '0.75rem', padding: '1rem', backgroundColor: 'var(--bg-soft)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)' }}>
            <div style={{ fontSize: '0.875rem', fontWeight: 700, marginBottom: '0.85rem', color: 'var(--text-primary)' }}>Reporting</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <TimezoneSelect
                label="Provider Timezone"
                name="reporting_game_provider_timezone"
                value={formData.reporting_game_provider_timezone}
                onChange={handleInputChange}
                disabled={!canEditReporting}
              />
              <TimezoneSelect
                label="BO Timezone"
                name="reporting_bo_timezone"
                value={formData.reporting_bo_timezone}
                onChange={handleInputChange}
                disabled={!canEditReporting}
              />
              <div>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600 }}>Company Win Indicator</label>
                <select
                  name="reporting_company_win_indicator"
                  className="input"
                  value={formData.reporting_company_win_indicator}
                  onChange={handleInputChange}
                  disabled={!canEditReporting}
                >
                  <option value="">Select Indicator</option>
                  <option value="positive">Positive</option>
                  <option value="negative">Negative</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600 }}>Player Win Indicator</label>
                <select
                  name="reporting_player_win_indicator"
                  className="input"
                  value={formData.reporting_player_win_indicator}
                  onChange={handleInputChange}
                  disabled={!canEditReporting}
                >
                  <option value="">Select Indicator</option>
                  <option value="positive">Positive</option>
                  <option value="negative">Negative</option>
                </select>
              </div>
              <div style={{ gridColumn: '1 / span 2' }}>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600 }}>Remarks</label>
                <textarea
                  name="reporting_remarks"
                  className="input"
                  value={formData.reporting_remarks}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Reporting remarks"
                  disabled={!canEditReporting}
                  style={{ resize: 'vertical' }}
                />
              </div>
            </div>
          </div>
          )}
          <div style={{ gridColumn: '1 / span 2', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', backgroundColor: 'var(--bg-soft)', borderRadius: 'var(--radius-md)' }}>
              <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Topup Options</span>
              <label className="switch">
                <input type="checkbox" name="topup_options" checked={!!formData.topup_options} onChange={handleInputChange} />
                <span className="slider"></span>
              </label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', backgroundColor: 'var(--bg-soft)', borderRadius: 'var(--radius-md)' }}>
              <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Status</span>
              <label className="switch">
                <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleInputChange} />
                <span className="slider"></span>
              </label>
            </div>
          </div>
          <div style={{ gridColumn: '1 / span 2', marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600 }}>BO Credential Keys</label>
            <div style={{ marginBottom: '0.75rem' }}>
              <input
                type="url"
                name="reporting_provider_bo_link"
                className="input"
                value={formData.reporting_provider_bo_link}
                onChange={handleInputChange}
                placeholder="Provider BO Link"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 0.7fr) minmax(220px, 1fr) auto', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
              <input
                type="text"
                className="input"
                placeholder="e.g. bo_user"
                value={boCredentialKeyInput}
                onChange={(e) => setBoCredentialKeyInput(e.target.value)}
              />
              <input
                type="text"
                className="input"
                placeholder="Default value (optional)"
                value={boCredentialDefaultInput}
                onChange={(e) => setBoCredentialDefaultInput(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={addBoCredentialKey}
                style={{ padding: '0.45rem 0.9rem', whiteSpace: 'nowrap' }}
              >Add Key</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.5rem 0.75rem', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--bg-soft)' }}>
              {normalizeCredentialFields(formData.bo_credential_keys).length === 0 ? (
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>No keys added</span>
              ) : normalizeCredentialFields(formData.bo_credential_keys).map((field) => (
                <div key={field.key} style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 0.7fr) minmax(220px, 1fr) auto', gap: '0.5rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: '9999px', padding: '0.35rem 0.65rem', minWidth: 0, overflowWrap: 'anywhere' }}>
                    {field.key}
                  </span>
                  <input
                    type="text"
                    className="input"
                    placeholder="Default value (optional)"
                    value={field.defaultValue || ''}
                    onChange={(e) => updateBoCredentialDefault(field.key, e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => removeBoCredentialKey(field.key)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1 }}
                    title="Remove key"
                  >×</button>
                </div>
              ))}
            </div>
          </div>
          <div style={{ gridColumn: '1 / span 2', marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600 }}>API Credential Keys</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 0.7fr) minmax(220px, 1fr) auto', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
              <input
                type="text"
                className="input"
                placeholder="e.g. api_key"
                value={credentialKeyInput}
                onChange={(e) => setCredentialKeyInput(e.target.value)}
              />
              <input
                type="text"
                className="input"
                placeholder="Default value (optional)"
                value={credentialDefaultInput}
                onChange={(e) => setCredentialDefaultInput(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={addCredentialKey}
                style={{ padding: '0.45rem 0.9rem', whiteSpace: 'nowrap' }}
              >Add Key</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.5rem 0.75rem', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--bg-soft)' }}>
              {normalizeCredentialFields(formData.api_credential_keys).length === 0 ? (
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>No keys added</span>
              ) : normalizeCredentialFields(formData.api_credential_keys).map((field) => (
                <div key={field.key} style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 0.7fr) minmax(220px, 1fr) auto', gap: '0.5rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: '9999px', padding: '0.35rem 0.65rem', minWidth: 0, overflowWrap: 'anywhere' }}>
                    {field.key}
                  </span>
                  <input
                    type="text"
                    className="input"
                    placeholder="Default value (optional)"
                    value={field.defaultValue || ''}
                    onChange={(e) => updateCredentialDefault(field.key, e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => removeCredentialKey(field.key)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1 }}
                    title="Remove key"
                  >×</button>
                </div>
              ))}
            </div>
          </div>
          <div style={{ gridColumn: '1 / span 2', marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600 }}>Callback URLs</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
              <input
                type="text"
                className="input"
                placeholder="Callback name (e.g. balance, bet)"
                value={callbackNameInput}
                onChange={(e) => setCallbackNameInput(e.target.value)}
              />
              <input
                type="text"
                className="input"
                placeholder="Path (e.g. /callback/bet)"
                value={callbackPathInput}
                onChange={(e) => setCallbackPathInput(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={addCallbackUrl}
                style={{ padding: '0.45rem 0.9rem', whiteSpace: 'nowrap' }}
              >Add</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', padding: '0.5rem 0.75rem', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--bg-soft)' }}>
              {(formData.callback_urls || []).length === 0 ? (
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>No callback URLs added</span>
              ) : (formData.callback_urls || []).map((cb) => (
                <div key={cb.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                    <strong style={{ color: 'var(--text-primary)' }}>{cb.name}</strong>
                    <span style={{ marginLeft: '0.5rem', color: 'var(--text-muted)' }}>{cb.path}</span>
                  </div>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => removeCallbackUrl(cb.name)}
                    style={{ padding: '0.25rem 0.6rem', fontSize: '0.75rem', color: 'var(--error)' }}
                  >Remove</button>
                </div>
              ))}
            </div>
          </div>
          <div style={{ gridColumn: '1 / span 2', marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8125rem', fontWeight: 600 }}>Upload Documents</label>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
              <label
                className="btn btn-secondary"
                style={{ padding: '0.55rem 0.95rem', cursor: 'pointer' }}
              >
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  multiple
                  onChange={handleDocumentUpload}
                  style={{ display: 'none' }}
                />
                Choose Files
              </label>
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                Supported: PDF, Word, Excel, JPG, PNG (max 10MB each). Files will upload to S3 only after you save.
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.75rem', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--bg-soft)' }}>
              {(formData.documents || []).length === 0 ? (
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>No documents added</span>
              ) : (formData.documents || []).map((doc) => (
                <div key={getDocumentKey(doc)} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0, flex: '1 1 320px' }}>
                    <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-primary)', wordBreak: 'break-word' }}>{doc.original_name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {formatFileSize(doc.size)}
                      {doc.uploaded_at ? ` • ${new Date(doc.uploaded_at).toLocaleString()}` : ' • Pending upload'}
                    </div>
                    <input
                      type="text"
                      className="input"
                      placeholder="Document label (e.g. Agreement, API Spec)"
                      value={doc.label || ''}
                      onChange={(e) => handleDocumentLabelChange(getDocumentKey(doc), e.target.value)}
                      style={{ marginTop: '0.5rem' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    {doc.file_path ? (
                      <a
                        className="btn"
                        href={gameService.getMasterProviderDocumentUrl(doc)}
                        target="_blank"
                        rel="noreferrer"
                        style={{ padding: '0.25rem 0.6rem', fontSize: '0.75rem', color: 'var(--accent)', textDecoration: 'none' }}
                      >
                        View
                      </a>
                    ) : (
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Uploads on save</span>
                    )}
                    <button
                      type="button"
                      className="btn"
                      onClick={() => removeDocument(doc)}
                      style={{ padding: '0.25rem 0.6rem', fontSize: '0.75rem', color: 'var(--error)' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ gridColumn: '1 / span 2', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', paddingTop: '1rem', borderTop: '1px solid var(--border-light)' }}>
            <button type="button" className="btn" onClick={() => closeModal()} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving...' : (editId ? 'Update Master Provider' : 'Create Master Provider')}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default MasterProviders;
